import { APP_BASE_HREF, TitleCasePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
  withXhr,
} from '@angular/common/http';
import {
  APP_ID,
  inject,
  LOCALE_ID,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { EbbBootstrapComponent } from '@app/browser-extension/components/ebb-bootstrap/ebb-bootstrap.component';
import { HttpConfigInterceptor } from '@app/browser-extension/core/interceptor/http-config.interceptor';
import { initExtensionAppState } from '@app/browser-extension/extension-initializer';
import { EbbAuthUserService } from '@app/browser-extension/services/ebb-auth-user.service';
import { EbbAuthService } from '@app/browser-extension/services/ebb-auth.service';
import { ExtensionLocalizationService } from '@app/browser-extension/services/extension-localization.service';
import { SharedProvidersModule } from '@app/shared/shared-providers.module';

import {
  ApplicationId,
  BrandingService,
  CachingInterceptor,
  ConfigurationService,
  DgTranslateDynamicLoader,
  LDFlagsService,
  LocalizationService,
  MiniProfilerInterceptor,
  NgxHttpClient,
  ORG_TYPE_TOKEN,
  WebEnvironmentService,
  WindowToken,
  XSRFTokenInterceptor,
} from '@degreed/lxp/services';
import {
  AuthInterceptor,
  AuthService,
} from '@degreed/lxp/services/authorization';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';

@NgModule({
  declarations: [EbbBootstrapComponent],
  bootstrap: [EbbBootstrapComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule.forRoot([
      {
        path: 'degreed-button/app',
        loadChildren: () =>
          import('./browser-extension.module').then(
            (m) => m.BrowserExtensionModule
          ),
      },
    ]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: DgTranslateDynamicLoader,
      },
    }),
    SharedProvidersModule,
  ],
  providers: [
    // translation
    {
      provide: ORG_TYPE_TOKEN,
      useFactory: (authService: AuthService) => () =>
        authService.authUser?.defaultOrgInfo?.type,
      deps: [AuthService],
    },
    // interceptors
    { provide: HTTP_INTERCEPTORS, useClass: XSRFTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MiniProfilerInterceptor,
      multi: true,
    },

    TitleCasePipe,
    NgxHttpClient,
    ExtensionLocalizationService,
    { provide: LocalizationService, useExisting: ExtensionLocalizationService },
    { provide: AuthService, useExisting: EbbAuthUserService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      deps: [LocalizationService],
      useFactory: (localizationService: ExtensionLocalizationService) => {
        const userLocale = localizationService.userExtensionLocale;
        // lazy-load the locales which we need for localizing dates in some areas of the app
        localizationService.loadLocale(userLocale);

        return userLocale;
      },
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (win: Window) => win.origin,
      deps: [WindowToken],
    },
    provideAppInitializer(() => {
      const initializerFn = initExtensionAppState(
        inject(WebEnvironmentService),
        inject(AuthService),
        inject(EbbAuthService),
        inject(TranslateService),
        inject(ConfigurationService),
        inject(ExtensionLocalizationService),
        inject(LDFlagsService),
        inject(BrandingService)
      );
      return initializerFn();
    }),
    {
      provide: APP_ID,
      useValue: ApplicationId.Extension,
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi(), withJsonpSupport()),
  ],
})
export class AppBrowserExtensionBootstrapModule {}
